import moment from "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import { batch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { AppStateLogin } from "../redux/auth";
import { deviceInclude } from "../redux/devices";
import { NodeType, OrganizationNode } from "../redux/organization";
import {
  isExistAllNodes,
  isSelectedEndNodeISActualEND,
  rootNode,
  userAssessDefaultENDNode,
} from "../redux/organization/selectors";
import { residentInclude } from "../redux/residents";
import { IAppState } from "../redux/types";
import { Dispatcher } from "../service/RootDispather";
import SoundRepeat from "./SoundRepeat";

import { IdentifyUserWithFlags } from "./featureFlags/FeatureFlags";
import { usePrevious } from "./hooks/hooks";
import PlanRetriever from "./PlanRetriever";
import {
  DeviceInfoProvider,
  DeviceNativeInteractor,
  setLoggedIn,
} from "./pushNotification/NativeInteractor";
import ConditionalPreloader from "../components/preloader/Preloaders";
import { TagTypes } from "../redux/users";
import { AppStateUserActivity } from "../redux/userActivity/types";

export const ErorrCheck = React.memo(
  ({ children }: { children: any }): JSX.Element => {
    const { baseNodeId } = useSelector((state: IAppState) => state.userProfile);
    const { organizationServiceError, residentServiceError, socketsWorking } =
      useSelector((state: IAppState) => state.healthCheck);
    const organizationPrevIsError = usePrevious(organizationServiceError);

    const residentPrevIsError = usePrevious(residentServiceError);
    const dispatcher = Dispatcher();
    const { isAuthenticated } = useSelector<IAppState, AppStateLogin>(
      (state) => state.auth
    );
    const socketWasWorking = usePrevious(socketsWorking);
    const { isRunning } = useSelector<IAppState, AppStateUserActivity>(
      (state) => state.userActivity
    );
    const retrieveResidentsData = useCallback(() => {
      batch(() => {
        dispatcher.getOrganizationInfo();
        dispatcher.getResidents({
          include: [residentInclude.devices, residentInclude.nodes],
        });
        dispatcher.getDevices();
      });
      batch(() => {
        dispatcher.getResidentsRulesAlertsStatuses();
        dispatcher.getDevicesStatuses({ include: [deviceInclude.statuses] });
        dispatcher.getVitalsUserNearby();
        dispatcher.getRepeatAlert();
        dispatcher.getResidentsConfigurations();
        dispatcher.getActivitiesFeedback();

        dispatcher.getUsers({ includeActivityStatus: true, includeRole: true });
        dispatcher.getUsersMobileDevices();
      });
    }, [dispatcher]);

    const retrieveResidentsDataAfterError = useCallback(() => {
      batch(() => {
        dispatcher.getOrganizationInfo(false);
        dispatcher.getResidents(
          {
            include: [residentInclude.devices, residentInclude.nodes],
          },
          false
        );
        dispatcher.getDevices(undefined, false);
      });
      batch(() => {
        dispatcher.getResidentsRulesAlertsStatuses();
        dispatcher.getDevicesStatuses({ include: [deviceInclude.statuses] });
        dispatcher.getVitalsUserNearby();
        dispatcher.getResidentsConfigurations();
        // dispatcher.getUsers({ includeActivityStatus: true, includeRole: true });
        dispatcher.getUsersMobileDevices();
      });
    }, [dispatcher]);
    useEffect(() => {
      if (
        isAuthenticated === true &&
        socketsWorking === true &&
        socketWasWorking === false
      ) {
        //connection restored
        retrieveResidentsDataAfterError();
      }
    }, [
      socketsWorking,
      isAuthenticated,
      socketWasWorking,
      dispatcher,
      retrieveResidentsDataAfterError,
    ]);

    useEffect(() => {
      if (isAuthenticated === true && organizationServiceError === false) {
        retrieveResidentsData();
      }
    }, [isAuthenticated, organizationServiceError, retrieveResidentsData]);

    useEffect(() => {
      if (isRunning && organizationServiceError)
        dispatcher.stopSendingUserActivity();
    }, [isRunning, organizationServiceError, dispatcher]);

    useEffect(() => {
      if (isAuthenticated === true && organizationServiceError === false) {
        batch(() => {
          if (baseNodeId !== -1) dispatcher.startSendingUserActivity();
        });
      }
    }, [isAuthenticated, baseNodeId, organizationServiceError, dispatcher]);
    //organization service up
    useEffect(() => {
      if (
        isAuthenticated === true &&
        organizationPrevIsError === true &&
        organizationServiceError === false
      ) {
        retrieveResidentsDataAfterError();
      }
    }, [
      isAuthenticated,
      organizationPrevIsError,
      organizationServiceError,
      retrieveResidentsDataAfterError,
    ]);
    //residents service up
    useEffect(() => {
      if (
        isAuthenticated === true &&
        residentPrevIsError === true &&
        residentServiceError === false
      ) {
        retrieveResidentsDataAfterError();
      }
    }, [
      isAuthenticated,
      residentPrevIsError,
      residentServiceError,
      retrieveResidentsDataAfterError,
    ]);

    return <>{children}</>;
  }
);
ErorrCheck.displayName = "ErorrCheck";
const CheckToken = ({ children }: { children: any }): JSX.Element => {
  const { isAuthenticated } = useSelector<IAppState, AppStateLogin>(
    (state) => state.auth
  );
  const {
    endNodeId,
    selectedNodeId,
    baseNodeId,
    monitorNodeId,
    notificationSound,
  } = useSelector((state: IAppState) => state.userProfile);

  const dispatcher = Dispatcher();
  const baseEndNodeID = useSelector(userAssessDefaultENDNode);
  const { isMonitorNode, isEndNode, isSelectedNode } =
    useSelector(isExistAllNodes);
  ///fucked up with changing logic each fucking time
  const isActuallyEnd = useSelector(isSelectedEndNodeISActualEND);

  const rootNodeId = useSelector(rootNode);

  useEffect(() => {
    if (
      (endNodeId === undefined ||
        !isEndNode ||
        (isEndNode as OrganizationNode)?.type !== NodeType.Group) &&
      baseEndNodeID !== undefined &&
      isAuthenticated
    )
      dispatcher.changeSelectedEndNode(baseEndNodeID);
  }, [baseEndNodeID, isAuthenticated, dispatcher, endNodeId, isEndNode]);

  useEffect(() => {
    if (isActuallyEnd && baseEndNodeID !== undefined && isAuthenticated)
      dispatcher.changeSelectedEndNode(baseEndNodeID);
  }, [baseEndNodeID, isActuallyEnd, isAuthenticated, dispatcher]);

  useEffect(() => {
    if (
      (selectedNodeId === undefined || !isSelectedNode) &&
      baseNodeId !== undefined &&
      isAuthenticated
    )
      dispatcher.changeSelectedNode(baseNodeId);
  }, [selectedNodeId, baseNodeId, isAuthenticated, dispatcher, isSelectedNode]);

  useEffect(() => {
    if (
      (monitorNodeId === undefined ||
        monitorNodeId === rootNodeId ||
        !isMonitorNode) &&
      baseEndNodeID !== undefined &&
      isAuthenticated
    )
      dispatcher.changeMonitorNode(baseEndNodeID);
  }, [
    monitorNodeId,
    baseEndNodeID,
    isAuthenticated,
    dispatcher,
    rootNodeId,
    isMonitorNode,
  ]);

  const { subscribeData } = useSelector(
    (state: IAppState) => state.healthCheck
  );
  const firstNotifSound = usePrevious(notificationSound);
  useEffect(() => {
    if (
      firstNotifSound === undefined &&
      notificationSound === false &&
      subscribeData &&
      subscribeData.mobileId
    ) {
      dispatcher.unsubscribeNotification({ mobileId: subscribeData.mobileId });
    }
  }, [firstNotifSound, dispatcher, subscribeData, notificationSound]);
  return (
    <SilentCheckAuth>
      <IdentifyUserWithFlags>
        <ErorrCheck>
          <ConditionalPreloader />
          {isAuthenticated && (
            <>
              <DeviceInfoProvider>
                <DeviceNativeInteractor />
              </DeviceInfoProvider>
              <SoundRepeat />
              <PlanRetriever />
              <DeviceAsWatch />
            </>
          )}
          {children}
        </ErorrCheck>
      </IdentifyUserWithFlags>
    </SilentCheckAuth>
  );
};

const SilentCheckAuth = React.memo(
  ({ children }: { children: any }): JSX.Element => {
    const { isLoggedOut, isAuthenticated, redirectTo, dateWhenExpire } =
      useSelector((state: IAppState) => state.auth);

    const rootDispatcher = Dispatcher();
    // console.log('SilentCheckAuthNew ', isAuthenticated, dateWhenExpire);
    const [haschecked, sethaschecked] = useState(false);
    useEffect(() => {
      if (isAuthenticated && dateWhenExpire && !haschecked) {
        rootDispatcher.getUsersProfile();
        sethaschecked(true);
        console.log("getProfileLogIn");

        setLoggedIn(true);
      }
    }, [dateWhenExpire, isAuthenticated, rootDispatcher, haschecked]);
    const history = useHistory();
    useEffect(() => {
      if (isLoggedOut) {
        if (redirectTo && redirectTo !== "") history.push(redirectTo);
        sethaschecked(false);
      }
    }, [isLoggedOut, redirectTo, history]);

    useEffect(() => {
      let interval;
      if (dateWhenExpire !== undefined) {
        const timeUntil = Math.min(
          moment(dateWhenExpire).diff(moment(), "milliseconds") - 1000 * 60 * 5,
          2 ** 31 - 1000 * 60 * 5
        ); //5 mins before
        interval = setInterval(() => {
          try {
            if (isAuthenticated) {
              rootDispatcher.rotateToken();
            }
          } catch (e) {
            console.error(e);
          }
        }, timeUntil);
      }
      return () => {
        if (interval) clearInterval(interval);
      };
    }, [dateWhenExpire, isAuthenticated, rootDispatcher]);

    const { email, id } = useSelector((state: IAppState) => state.userProfile);
    const { isNeedToBeLoginNotifyed } = useSelector(
      (state: IAppState) => state.healthCheck
    );
    useEffect(() => {
      if (
        email !== undefined &&
        id !== undefined &&
        isNeedToBeLoginNotifyed === true
      ) {
        console.log("weve actually logged in");
        rootDispatcher.notifyLoginLogout(true, { email, userId: id });
      }
    }, [email, id, isNeedToBeLoginNotifyed, rootDispatcher]);

    return <>{children}</>;
  }
);
SilentCheckAuth.displayName = "SilentCheckAuth";
const DeviceAsWatch = React.memo(() => {
  const rootDispatcher = Dispatcher();
  const { id } = useSelector((state: IAppState) => state.userProfile);
  const { isNeedToBeLoginNotifyed, subscribeData, useMobileDeviceAsWatch } =
    useSelector((state: IAppState) => state.healthCheck);

  useEffect(() => {
    if (
      // isNeedToBeLoginNotifyed === true &&
      id !== undefined &&
      subscribeData?.mobileId &&
      subscribeData?.mobileId !== "" &&
      !useMobileDeviceAsWatch
    ) {
      rootDispatcher.subscribeUserMobileDeviceAsTag({
        userId: id,
        deviceId: subscribeData?.mobileId,
        serialNumber: subscribeData?.mobileId,
        deviceType: TagTypes.Watch, //(subscribeData?.deviceType as TagTypes) || TagTypes.Web,
        metadata: subscribeData?.metadata || "",
      });
    }
  }, [
    subscribeData,
    id,
    isNeedToBeLoginNotifyed,
    useMobileDeviceAsWatch,
    rootDispatcher,
  ]);
  // const [isDeviceAlreadyTag, setisDeviceAlreadyTag] = useState(false);
  // useEffect(() => {
  //   //if we're already using this device as mobile ID
  //   if (
  //     subscribeData?.mobileId &&
  //     subscribeData?.mobileId !== "" &&
  //     userMobileDevice.deviceId === subscribeData.mobileId &&
  //     !isDeviceAlreadyTag
  //   ) {
  //     console.log("Its a tag!!!");
  //     rootDispatcher.setUserMobileDeviceAsTag();
  //     setisDeviceAlreadyTag(true);
  //   }
  // }, [
  //   subscribeData,
  //   isDeviceAlreadyTag,
  //   rootDispatcher
  // ]);

  return null;
});
DeviceAsWatch.displayName = "DeviceAsWatch";
export default React.memo(CheckToken);
